import { useCallback, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

import type { SwipeDirection } from "./types";

/** Distance past which a release commits, rather than springing back. */
const COMMIT_DISTANCE = 96;

/** A flick this fast commits regardless of distance, in px per millisecond. */
const FLICK_VELOCITY = 0.6;

/** Below this the drag is not yet interpreted as pointing anywhere. */
const INTENT_DISTANCE = 24;

export interface SwipeGestureState {
  dx: number;
  dy: number;
  /** True while a pointer is down and moving the card. */
  isDragging: boolean;
  /** Where the current drag would land, or null while under the intent floor. */
  intent: SwipeDirection | "down" | null;
}

export interface SwipeGesture extends SwipeGestureState {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  /** Card transform for the current drag. */
  style: { transform: string; transition: string };
}

const IDLE: SwipeGestureState = {
  dx: 0,
  dy: 0,
  isDragging: false,
  intent: null,
};

/**
 * Which way a drag points.
 *
 * Horizontal wins ties by a margin, because a thumb arcs sideways: a swipe
 * meant as "left" routinely carries 30-40px of vertical drift, and reading
 * that as "up" writes the wrong answer to a real contact.
 */
export const directionOf = (
  dx: number,
  dy: number,
): SwipeDirection | "down" | null => {
  if (Math.hypot(dx, dy) < INTENT_DISTANCE) return null;
  if (Math.abs(dx) * 1.3 >= Math.abs(dy)) return dx > 0 ? "right" : "left";
  return dy < 0 ? "up" : "down";
};

/**
 * Pointer-driven card dragging.
 *
 * Hand-rolled rather than pulled from a library: the whole behaviour is one
 * transform and one threshold, and the app has no animation dependency to
 * justify adding for it.
 */
export function useSwipeGesture(
  onCommit: (direction: SwipeDirection | "down") => void,
  enabled: boolean,
): SwipeGesture {
  const [state, setState] = useState<SwipeGestureState>(IDLE);
  const origin = useRef<{ x: number; y: number; t: number } | null>(null);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled) return;
      // Let links, buttons and text selection inside the card behave normally.
      if ((event.target as HTMLElement).closest("a,button")) return;
      origin.current = { x: event.clientX, y: event.clientY, t: Date.now() };
      event.currentTarget.setPointerCapture(event.pointerId);
      setState({ ...IDLE, isDragging: true });
    },
    [enabled],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const start = origin.current;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    setState({ dx, dy, isDragging: true, intent: directionOf(dx, dy) });
  }, []);

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const start = origin.current;
      origin.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (!start) return;

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const distance = Math.hypot(dx, dy);
      const velocity = distance / Math.max(1, Date.now() - start.t);
      const direction = directionOf(dx, dy);

      setState(IDLE);
      if (
        direction &&
        (distance > COMMIT_DISTANCE || velocity > FLICK_VELOCITY)
      ) {
        onCommit(direction);
      }
    },
    [onCommit],
  );

  return {
    ...state,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    style: {
      transform: `translate3d(${state.dx}px, ${state.dy}px, 0) rotate(${state.dx * 0.05}deg)`,
      transition: state.isDragging
        ? "none"
        : "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    },
  };
}

export { COMMIT_DISTANCE, INTENT_DISTANCE };
